import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MagnifyingGlass, Funnel, X } from '@phosphor-icons/react';
import ProductCard from '../components/ProductCard';
import { getItems, getCategories } from '../utils/api';
import { debounce } from '../utils/helpers';

const Shop = () => {
  const { categoryId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [selectedCategoryId, setSelectedCategoryId] = useState(categoryId ? parseInt(categoryId) : null);
  const [showFilters, setShowFilters] = useState(false);

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await getCategories();
        setCategories(res.data.data || []);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, []);

  // Update selected category when URL param changes
  useEffect(() => {
    setSelectedCategoryId(categoryId ? parseInt(categoryId) : null);
  }, [categoryId]);

  // Fetch products
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const params = {
          page: pagination.page,
          limit: 12,
        };
        
        if (selectedCategoryId) {
          params.category_id = selectedCategoryId;
        }
        if (searchTerm) {
          params.search = searchTerm;
        }
        
        const res = await getItems(params);
        setProducts(res.data.data || []);
        setPagination(prev => ({
          ...prev,
          totalPages: res.data.pagination?.total_pages || 1,
          total: res.data.pagination?.total || 0
        }));
      } catch (error) {
        console.error('Error fetching products:', error);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProducts();
  }, [selectedCategoryId, searchTerm, pagination.page]);

  // Debounced search
  const handleSearch = debounce((value) => {
    setSearchTerm(value);
    setPagination(prev => ({ ...prev, page: 1 }));
    if (value) {
      setSearchParams({ search: value });
    } else {
      setSearchParams({});
    }
  }, 300);

  const handleCategorySelect = (catId) => {
    setSelectedCategoryId(catId === selectedCategoryId ? null : catId);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setSelectedCategoryId(null);
    setSearchTerm('');
    setSearchParams({});
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  return (
    <div className="min-h-screen bg-background-default">
      {/* Page Header */}
      <section className="bg-background-subtle py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="font-display text-4xl md:text-5xl text-secondary mb-4">
              {selectedCategoryId ? (categories.find(c => c.id === selectedCategoryId)?.name || 'Collection') : 'Our Collection'}
            </h1>
            <p className="font-body text-text-secondary max-w-xl mx-auto">
              Discover our exquisite range of handcrafted jewelry pieces
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters & Products */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Filter Bar */}
          <div className="flex flex-col md:flex-row gap-4 mb-8 items-center justify-between">
            {/* Search */}
            <div className="relative w-full md:w-80">
              <MagnifyingGlass size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted" />
              <input
                type="text"
                placeholder="Search products..."
                data-testid="search-input"
                defaultValue={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-border-default focus:border-primary focus:outline-none font-body text-sm transition-colors"
              />
            </div>
            
            {/* Filter Toggle & Active Filters */}
            <div className="flex items-center gap-4">
              {(selectedCategoryId || searchTerm) && (
                <button
                  onClick={clearFilters}
                  data-testid="clear-filters-btn"
                  className="flex items-center gap-2 text-sm text-text-secondary hover:text-primary transition-colors"
                >
                  <X size={16} />
                  Clear Filters
                </button>
              )}
              
              <button
                onClick={() => setShowFilters(!showFilters)}
                data-testid="filter-toggle-btn"
                className="md:hidden flex items-center gap-2 btn-secondary py-2 px-4"
              >
                <Funnel size={18} />
                Filters
              </button>
              
              <span className="font-body text-sm text-text-secondary">
                {pagination.total} products
              </span>
            </div>
          </div>

          <div className="flex gap-8">
            {/* Sidebar Filters - Desktop */}
            <aside className={`w-64 flex-shrink-0 hidden md:block`}>
              <div className="sticky top-28">
                <h3 className="font-display text-lg text-secondary mb-4">Categories</h3>
                <ul className="space-y-2">
                  <li>
                    <button
                      onClick={() => handleCategorySelect(null)}
                      data-testid="filter-all"
                      className={`font-body text-sm w-full text-left py-2 px-3 transition-colors ${
                        !selectedCategoryId ? 'bg-primary/10 text-primary' : 'text-text-secondary hover:text-primary'
                      }`}
                    >
                      All Products
                    </button>
                  </li>
                  {categories.map((cat) => (
                    <li key={cat.id}>
                      <button
                        onClick={() => handleCategorySelect(cat.id)}
                        data-testid={`filter-${cat.name.toLowerCase()}`}
                        className={`font-body text-sm w-full text-left py-2 px-3 transition-colors ${
                          selectedCategoryId === cat.id 
                            ? 'bg-primary/10 text-primary' 
                            : 'text-text-secondary hover:text-primary'
                        }`}
                      >
                        {cat.name}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>

            {/* Mobile Filters */}
            {showFilters && (
              <div className="fixed inset-0 bg-black/50 z-50 md:hidden" onClick={() => setShowFilters(false)}>
                <motion.div
                  initial={{ x: '100%' }}
                  animate={{ x: 0 }}
                  exit={{ x: '100%' }}
                  className="absolute right-0 top-0 bottom-0 w-80 bg-white p-6"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="font-display text-xl">Filters</h3>
                    <button onClick={() => setShowFilters(false)}>
                      <X size={24} />
                    </button>
                  </div>
                  
                  <h4 className="font-display text-lg text-secondary mb-4">Categories</h4>
                  <ul className="space-y-2">
                    <li>
                      <button
                        onClick={() => { handleCategorySelect(null); setShowFilters(false); }}
                        className={`font-body text-sm w-full text-left py-2 px-3 ${
                          !selectedCategoryId ? 'bg-primary/10 text-primary' : 'text-text-secondary'
                        }`}
                      >
                        All Products
                      </button>
                    </li>
                    {categories.map((cat) => (
                      <li key={cat.id}>
                        <button
                          onClick={() => { handleCategorySelect(cat.id); setShowFilters(false); }}
                          className={`font-body text-sm w-full text-left py-2 px-3 ${
                            selectedCategoryId === cat.id 
                              ? 'bg-primary/10 text-primary' 
                              : 'text-text-secondary'
                          }`}
                        >
                          {cat.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              </div>
            )}

            {/* Products Grid */}
            <div className="flex-1">
              {loading ? (
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="animate-pulse">
                      <div className="aspect-[3/4] bg-gray-200" />
                      <div className="p-4 space-y-2">
                        <div className="h-4 bg-gray-200 rounded" />
                        <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : products.length === 0 ? (
                <div className="text-center py-20">
                  <h3 className="font-display text-2xl text-secondary mb-2">No products found</h3>
                  <p className="font-body text-text-secondary mb-6">
                    Try adjusting your search or filter criteria
                  </p>
                  <button onClick={clearFilters} className="btn-primary">
                    Clear Filters
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
                    {products.map((product) => (
                      <ProductCard key={product.id} product={product} />
                    ))}
                  </div>
                  
                  {/* Pagination */}
                  {pagination.totalPages > 1 && (
                    <div className="flex justify-center gap-2 mt-12">
                      {[...Array(pagination.totalPages)].map((_, i) => (
                        <button
                          key={i}
                          onClick={() => setPagination(prev => ({ ...prev, page: i + 1 }))}
                          data-testid={`page-${i + 1}`}
                          className={`w-10 h-10 font-body text-sm transition-colors ${
                            pagination.page === i + 1 
                              ? 'bg-primary text-white' 
                              : 'bg-white text-secondary hover:bg-primary/10'
                          }`}
                        >
                          {i + 1}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Shop;
